import { inject } from "mobx-react";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { AspectRatioEnum, ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { HotelHeartButton } from "../HotelHeartButton/HotelHeartButton";
import * as React from "react";
import { ILocalization } from "bernie-l10n";
import { LazyLoaderPlaceHolder } from "components/utility/LazyLoaderPlaceholderWrapper";
import { UitkGalleryCarousel } from "uitk-react-gallery-carousel";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import LazyLoad from "react-lazyload";
import { AnalyticsStore } from "bernie-core";

interface CardMediaOverallInfoProps {
  index: number;
  hotel: Hotel;
  thumbor?: ThumborStore;
  analytics?: AnalyticsStore;
  context: ExtendedContextStore;
  l10n: ILocalization;
  moduleName?: string;
  gallery?: Array<{ src: string; alt: string }>;
}

/**
 * Represents a {@see HotelCard}'s media (image, carousel, favorite button, etc.)
 * TODO: Clean up {@see Experiment.Blossom_Hotel_Image_Carousel} once finished
 */
export const CardMediaOverallInfo: React.FC<CardMediaOverallInfoProps> = inject(
  "thumbor",
  "analytics"
)(({ hotel, thumbor, analytics, context, l10n, moduleName = "hotels", gallery, index }) => {
  const { hotelName, hotelImageUrls } = hotel;
  const isMobile = !!(context && context.deviceInformation && context.deviceInformation.mobile);
  const width = isMobile ? 340 : 420;
  const height = isMobile ? 150 : 224;
  const hotelImageUrl =
    hotelImageUrls &&
    hotelImageUrls.y &&
    thumbor &&
    thumbor.safelyGenerateThumborizedImageUrl(
      hotelImageUrls.y,
      width,
      height,
      context,
      isMobile ? AspectRatioEnum.R_2_1 : AspectRatioEnum.R_21_9,
      true,
      isMobile ? 60 : 100
    );
  let hotelMedia;
  // Because we're lazy loading the experiment (carousel), we need this to test
  // in jest
  let dataTestId;

  // Analytics setup for the gallery carousel (such as pagination tracking)
  // Because this is lazyloaded, we test tracking in e2e which our coverage doesn't
  // track so we have to ignore this for coverage.
  /* istanbul ignore next */
  const analyticsConfig = analytics!.setupTracker(
    "hotels",
    "User interacts with hotel card's gallery carousel"
  );

  // Show gallery carousel if enabled and present, otherwise show the model's
  // hotel image or a placeholder
  if (isVariantEnabled(context, Experiment.Blossom_Hotel_Image_Carousel) && gallery) {
    hotelMedia = (
      <LazyLoad placeholder={<LazyLoaderPlaceHolder name={hotelName} />} offset={250}>
        <UitkGalleryCarousel
          analytics={analyticsConfig}
          carouselName={`${index}`} // For analytics
          images={gallery}
          showGalleryButton={false}
          allyHeadingText={l10n.formatText("destinationImageGallery.title", hotelName)}
        />
      </LazyLoad>
    );
    dataTestId = "Blossom_Hotel_Image_Carousel-experiment-carousel";
  } else {
    hotelMedia = <LazyLoadedImage src={hotelImageUrl} alt={hotelName} context={context} />;
  }

  return (
    <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
      <div data-testid={dataTestId}>{hotelMedia}</div>
      <HotelHeartButton moduleName={moduleName} l10n={l10n} hotel={hotel} />
    </UitkFigure>
  );
});
